import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { systemUsersRoute, usersRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { Privileges } from 'src/app/privileges/privileges';
import { environment } from 'src/environments/environment';
import { UserVerificationEmailRequestModel } from '../models/user-verification-email-request.model';
import { GetUserAccountId, GetUserAccountIdVariables } from '../queries/generated/GetUserAccountId';
import { GetUserDetail, GetUserDetailVariables } from '../queries/generated/GetUserDetail';
import { getUserAccountId, getUserDetail } from '../queries/user-detail.graphql';
import { UserDetailApiService } from '../services/user-detail-api.service';
import * as UserDetailActions from './user-detail.actions';
import { getUserDetailSelector } from './user-detail.selectors';

@Injectable()
export class UserDetailEffects {
   loadUserDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            UserDetailActions.userDetailLoad,
            UserDetailActions.saveUserDetailSuccess,
            UserDetailActions.userDetailCancel,
            UserDetailActions.unlockUserSuccess
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         mergeMap(([action, params]) => {
            const id = parseInt(params.id, 10);
            return this.apiService.query<GetUserAccountId, GetUserAccountIdVariables>({
               query: getUserAccountId,
               variables: { id },
            });
         }),
         map((result) => result.data.user.accountId),
         catchError((error) => {
            return of(UserDetailActions.loadUserDetailsFailure(error));
         }),
         withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
         concatMap(([accountId, params, route]) => {
            const path = route.url[0].path;
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetUserDetail, GetUserDetailVariables>({
                  query: getUserDetail,
                  variables: {
                     id,
                     extensionFilter: `accountId = ${accountId ?? 0}`,
                     extensionGroupFilter: `accountId = ${accountId ?? 0}`,
                  },
               })
               .pipe(
                  withLatestFrom(this.store.pipe(select(selectPrivileges))),
                  map(([userResult, privileges]) => {
                     if (
                        path === systemUsersRoute &&
                        userResult.data.user.account?.accountId !== environment.lcsAccountId
                     ) {
                        this.router.navigate([usersRoute, id], {
                           replaceUrl: true,
                        });
                        return UserDetailActions.userDetailRouteCorrected();
                     }

                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageUsersAndPrivileges_View,
                           path === systemUsersRoute ? 0 : userResult.data.user.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return UserDetailActions.userDetailAccessDenied();
                     }
                     if (path === systemUsersRoute) {
                        userResult.data.user.rolesConnection.items = userResult.data.user.rolesConnection.items.filter(
                           (role) => role.role.accountId == null
                        );
                        userResult.data.user.privilegesConnection.items =
                           userResult.data.user.privilegesConnection.items.filter(
                              (privilege) => privilege.accountId == null
                           );
                        return UserDetailActions.loadSystemUserDetailsSuccess({
                           data: userResult.data.user,
                           extensions: userResult.data.extensionsConnection,
                           extensionGroups: userResult.data.extensionGroupsConnection,
                        });
                     } else {
                        userResult.data.user.rolesConnection.items = userResult.data.user.rolesConnection.items.filter(
                           (role) => role.role.accountId != null
                        );
                        return UserDetailActions.loadUserDetailsSuccess({
                           data: userResult.data.user,
                           extensions: userResult.data.extensionsConnection,
                           extensionGroups: userResult.data.extensionGroupsConnection,
                        });
                     }
                  }),
                  catchError((error) => {
                     return of(UserDetailActions.loadUserDetailsFailure(error));
                  })
               );
         })
      );
   });

   saveUserDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(UserDetailActions.saveUserDetail),
         withLatestFrom(this.store.pipe(select(getUserDetailSelector))),
         switchMap(([action, user]) => {
            const apiCall = this.userDetailApiService.save(
               action.user,
               action.userFields,
               action.userPrivilegeFields,
               action.userCallRecordingExtensionFields,
               action.userCallRecordingExtensionGroupFields,
               user,
               action.isSystem
            );
            return apiCall.pipe(
               map((result) => {
                  return UserDetailActions.saveUserDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(UserDetailActions.saveUserDetailFailure(error));
               })
            );
         })
      );
   });

   saveUserDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(UserDetailActions.saveUserDetailSuccess),
            tap(() => {
               this.snackBar.open('User Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   resendVerificationEmail = createEffect(() => {
      return this.actions$.pipe(
         ofType(UserDetailActions.resendVerificationEmail),
         switchMap((action) => {
            const message = 'Are you sure you would like to resend the verification email?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Resend Verification Email?',
                  message: message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     const body: UserVerificationEmailRequestModel = {
                        emailAddress: action.emailAddress,
                        userId: action.userId,
                        username: action.username,
                     };
                     return this.userDetailApiService.resendVerificationEmail(body).pipe(
                        map(() => {
                           return UserDetailActions.resendVerificationEmailSuccess();
                        }),
                        catchError((error) => {
                           return of(UserDetailActions.resendVerificationEmailFailure(error));
                        })
                     );
                  }

                  return EMPTY;
               })
            );
         })
      );
   });

   resendEmailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(UserDetailActions.resendVerificationEmailSuccess),
            tap(() => {
               this.snackBar.open('Email Sent');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   deleteUser = createEffect(() => {
      return this.actions$.pipe(
         ofType(UserDetailActions.deleteUser),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this User?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete User?',
                  message: message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.userDetailApiService.deleteUser(action.userId).pipe(
                        map(() => {
                           return UserDetailActions.deleteUserSuccess(action);
                        }),
                        catchError((error) => {
                           return of(UserDetailActions.deleteUserFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   unlockUser = createEffect(() => {
      return this.actions$.pipe(
         ofType(UserDetailActions.unlockUser),
         switchMap((action) => {
            const message = 'Are you sure you would like to Unlock this User?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Unlock User?',
                  message: message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.userDetailApiService.unlockUser(action.userId).pipe(
                        map(() => {
                           return UserDetailActions.unlockUserSuccess({ name: action.name });
                        }),
                        catchError((error) => {
                           return of(UserDetailActions.unlockUserFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteUserSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(UserDetailActions.deleteUserSuccess),
            tap((action) => {
               this.router.navigate([usersRoute]);
               this.snackBar.open(`User '${action.name}' deleted`);
            })
         );
      },
      { dispatch: false }
   );

   unlockUserSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(UserDetailActions.unlockUserSuccess),
            tap((action) => {
               this.snackBar.open(`User '${action.name}' Unlocked`);
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private userDetailApiService: UserDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location
   ) {}
}
